import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getToken as fetchAppCheckToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import {
  browserLocalPersistence,
  getAuth,
  setPersistence,
  type Auth,
} from 'firebase/auth';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { env } from '@/lib/env';

// -----------------------------------------------------------------------------
// Firebase, web SDK.
//
// The mobile app is forced onto the native SDK because phone OTP needs a real
// reCAPTCHA host. Staff sign in with email + password, which has no such
// requirement, so the browser SDK is used directly here.
//
// PERSISTENCE: `browserLocalPersistence` is the SDK default and is kept
// deliberately. The SDK stores its own REFRESH token in IndexedDB and mints
// short-lived ID tokens from it in memory. This app never writes an ID token
// anywhere - no localStorage, no sessionStorage, no cookie. Reading one out of
// storage is the single most common way an admin token gets stolen by an XSS
// payload, and there is no reason to hand-roll what the SDK already does.
//
// Doc 15.6 additionally calls for an 8h session cap and a 30-minute idle
// timeout. Neither is implemented here - see "Not built yet" in the README.
// -----------------------------------------------------------------------------

export const firebaseApp: FirebaseApp = initializeApp({
  apiKey: env.firebase.apiKey,
  authDomain: env.firebase.authDomain,
  projectId: env.firebase.projectId,
  storageBucket: env.firebase.storageBucket,
  // Required by App Check, which attests an APP rather than a project and sends
  // this in its exchange URL. Without it the SDK requests
  // `/apps/undefined:exchangeRecaptchaEnterpriseToken` and gets a 400.
  appId: env.firebase.appId,
});

/**
 * App Check, backed by reCAPTCHA Enterprise.
 *
 * Registered BEFORE `getAuth()` and `getStorage()` below, and inside the one
 * module that hands out every Firebase handle this app has. The SDK attaches an
 * App Check token to a request only if a provider was registered by the time
 * that request is made, so doing it here makes the ordering impossible for a
 * caller to get wrong - there is no import path to Auth or Storage that does not
 * evaluate this line first.
 *
 * `isTokenAutoRefreshEnabled` keeps a token warm for the length of a staff
 * shift. Without it the first call after a token expires is rejected once and
 * only recovers on a retry the user has to trigger.
 *
 * Returns `null` only in development with no site key configured, in which case
 * App Check is off for that session. A production build cannot reach that
 * branch: `env.firebase.appCheckSiteKey` throws on a missing key when `PROD`.
 */
function startAppCheck(): AppCheck | null {
  const siteKey = env.firebase.appCheckSiteKey;

  if (!siteKey) {
    console.info('[app-check] No site key configured - App Check is off for this session.');
    return null;
  }

  if (!env.isProduction) {
    // reCAPTCHA Enterprise cannot attest `localhost`, so a dev session uses the
    // debug provider instead. With no token supplied the SDK mints one and logs
    // it; it has to be pasted into Firebase console -> App Check -> Manage debug
    // tokens once per browser profile. Setting `VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN`
    // in .env.local pins an already-registered token instead.
    const debugToken = import.meta.env.VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN?.trim();
    (
      globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }
    ).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken || true;
  }

  return initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const firebaseAppCheck: AppCheck | null = startAppCheck();

/**
 * Bounds a wait that would otherwise be unbounded.
 *
 * `getToken` waits on the reCAPTCHA script's load promise, and that promise is
 * only ever RESOLVED - if the script never loads, because an ad blocker or a
 * corporate proxy dropped it, nothing settles it and the await hangs forever.
 * The API client's own 20s timeout does not cover this: it starts its timer
 * after the token is in hand, so a hung token call is a spinner with no end.
 */
async function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), ms);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Reported failures, keyed by code.
 *
 * A dashboard fires a dozen queries at once and they would all fail the same
 * way; one console entry per distinct cause is the difference between a
 * diagnosis and a wall of noise.
 */
const reportedAppCheckErrors = new Set<string>();

function reportAppCheckFailure(error: unknown): void {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : error instanceof Error
        ? error.message
        : 'unknown';

  if (reportedAppCheckErrors.has(code)) return;
  reportedAppCheckErrors.add(code);

  // console.error, not a swallowed null. An earlier version of this returned
  // quietly and the only symptom was the API answering "App attestation is
  // required for this request" - which points at the server, names none of the
  // four things that actually cause it, and cost an afternoon.
  console.error(
    `[app-check] Could not get an attestation token (${code}). Every API call will be ` +
      'rejected with APP_CHECK_FAILED until this is fixed. Check, in order: the key in ' +
      'VITE_FIREBASE_APP_CHECK_SITE_KEY is a reCAPTCHA ENTERPRISE key (not v3); this app is ' +
      'registered in Firebase console -> App Check with the Enterprise provider; this ' +
      "domain is on the key's allowed-domains list; the reCAPTCHA Enterprise API is enabled " +
      'on the sectfit2025 Google Cloud project.',
    error,
  );
}

/** How long to wait for an attestation token before giving up on this request. */
const APP_CHECK_TOKEN_TIMEOUT_MS = 10_000;

/**
 * The current App Check attestation token, or null when one cannot be produced.
 *
 * Null rather than a throw, so one reCAPTCHA problem does not turn every screen
 * into "cannot reach the server". The request goes out without the header and
 * the API answers `APP_CHECK_FAILED`, which reads as "Request blocked" - and the
 * console carries the real cause.
 */
export async function getAppCheckToken(force = false): Promise<string | null> {
  if (!firebaseAppCheck) return null;

  try {
    const result = await withTimeout(
      fetchAppCheckToken(firebaseAppCheck, force),
      APP_CHECK_TOKEN_TIMEOUT_MS,
      'appCheck/timeout - the reCAPTCHA script did not load',
    );
    return result.token;
  } catch (error) {
    reportAppCheckFailure(error);
    return null;
  }
}

export const firebaseAuth: Auth = getAuth(firebaseApp);

/**
 * Storage, used only for staff avatars.
 *
 * The upload path is `users/{uid}/profile/{file}`, which `Backend/storage.rules`
 * already permits: `allow write: if isSelf(userId) && isImage() &&
 * withinSize(8)`. A staff member writing their own avatar satisfies all three,
 * so this needs no rules change - and the rules, not this client, are what
 * enforce it.
 */
export const firebaseStorage: FirebaseStorage = getStorage(firebaseApp);

// Fire-and-forget: `setPersistence` resolves before any sign-in can be attempted
// in practice, and the SDK queues auth calls behind it regardless.
void setPersistence(firebaseAuth, browserLocalPersistence);

/**
 * Maps Firebase Auth error codes to copy for a staff sign-in screen.
 *
 * `auth/invalid-credential` covers a wrong password, an unknown email and a
 * disabled-provider case. It is reported as one message on purpose: telling an
 * attacker which of those it was turns the login form into an account
 * enumeration oracle.
 */
export function describeFirebaseAuthError(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'Email or password is incorrect.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact a super admin.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Wait a few minutes and try again.';
    case 'auth/network-request-failed':
      return 'Cannot reach the authentication service. Check your connection.';
    case 'auth/operation-not-allowed':
      return 'Email and password sign-in is not enabled for this project.';
    case 'auth/requires-recent-login':
      return 'For security, sign out and back in before changing this.';
    case 'auth/weak-password':
      return 'That password is too weak. Use at least 8 characters.';
    default:
      return 'Something went wrong. Try again.';
  }
}
